import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';

import { Params } from '~libs/reduxUtils';

import ErrorResponse from '~components/ErrorResponse';
import { DateInput, Switch } from '~components/form-fields/inputs';
import Icon, { faChevronRight } from '~components/icon';

import { bulkUpdate } from '~appointments/actions/appointments';
import { useGetPublishEstimatesQuery, usePublishMutation } from '~weekly-planner/actions';

import { clear } from '~weekly-planner/reducers/unsaved';
import { updatePublishDate } from '~weekly-planner/reducers/weeklyPlanner';

import { selectAllChanges, selectAllChangesCount } from '~weekly-planner/selectors/unsaved';

interface ComponentProps {
  isOpen?: boolean;
  onClose: () => void;
  originalDate: string;
}

const Publish: React.FC<ComponentProps> = ({ isOpen = false, onClose, originalDate }) => {
  const dispatch = useAppDispatch();
  const changes = useAppSelector(selectAllChanges);
  const changesCount = useAppSelector(selectAllChangesCount);
  const [publishMutation] = usePublishMutation();

  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [sendSMS, setSendSMS] = useState<boolean>(false);
  const [newDate, setNewDate] = useState<string>('');

  // Fetch estimates only when the modal is open and a new date is selected
  const {
    data: estimates,
    isFetching: isEstimating,
    isError: isEstimateError,
    error: estimateError,
  } = useGetPublishEstimatesQuery({ published_date: newDate }, { skip: !isOpen || !newDate });

  const dateUpdate = (_id: string, value: string) => {
    setNewDate(value);
  };

  const onPublish = async () => {
    // Set dialog as publishing
    setIsPublishing(true);

    // Save any unsaved changes before publishing
    if (changesCount > 0) {
      const data: Params[] = Object.entries(changes).map(([, change]: [string, any]) => change);
      const { payload } = await dispatch(bulkUpdate(data));
      if (!payload?.success) {
        console.error('error, should be returning a response message in reducer');
        setIsPublishing(false);
        return;
      }
      dispatch(clear());
    }

    // Publish the date
    try {
      const res = await publishMutation({ published_date: newDate, send_sms: sendSMS }).unwrap();
      setIsPublishing(false);
      if (res?.success) {
        // Tag invalidation happens automatically via invalidatesTags in publishApi.publish
        dispatch(updatePublishDate(newDate));
        onClose();
      }
    } catch {
      // unwrap() re-throws on an API error. Without this catch isPublishing stays
      // true, which disables both the Publish AND Close buttons (the modal can only
      // be cleared by a page reload). Reset so the user can retry or close.
      setIsPublishing(false);
    }
  };

  const affectedUsers = estimates?.users ?? '-';
  const affectedAppointments = estimates?.appointments ?? '-';

  return (
    <Modal isOpen={isOpen} size="md">
      <ModalHeader className="bg-success text-white">Publish New Date</ModalHeader>
      <ModalBody>
        <div className="mb-2 pb-2 border-2 border-bottom border-success">
          Please confirm the date & following changes to publish:
        </div>
        {isEstimateError && (
          <ErrorResponse message={(estimateError as any)?.data?.message ?? 'Failed to fetch publish estimates.'} />
        )}
        <div className="p-2">
          <span className="mb-2 font-weight-bold">Publish Date</span>
          <div className="d-flex flex-row justify-content-center">
            <Input type="date" name="oldPublishDate" value={originalDate} disabled className="w-50" />
            <div className="align-bottom">
              <Icon icon={faChevronRight} className="align-bottom ms-2 me-2" />
            </div>
            <DateInput
              type="date"
              id="newPublishDate"
              name="newPublishDate"
              value={newDate}
              onChange={dateUpdate}
              disabled={isPublishing}
              className="w-50"
            />
          </div>
        </div>

        <div className="mt-4 mx-2">
          <Row>
            <Col sm="6">Unsaved Changes:</Col>
            <Col>{changesCount}</Col>
          </Row>
          <Row>
            <Col sm="6">Affected Care Workers:</Col>
            <Col>{isEstimating ? <Spinner type="border" size="sm" color="info" /> : <span>{affectedUsers}</span>}</Col>
          </Row>
          <Row>
            <Col sm="6">Affected Appointments:</Col>
            <Col>
              {isEstimating ? <Spinner type="border" size="sm" color="info" /> : <span>{affectedAppointments}</span>}
            </Col>
          </Row>
          <Row className="text-danger small mt-2">
            * Updating publish date will release all unsaved changes up to the new publish date.
          </Row>
        </div>
      </ModalBody>
      <ModalFooter className="d-block">
        <div className="d-flex justify-content-end align-items-center mb-2">
          <Label className="me-3 mb-1" for="sendSmsUpdate">
            Send SMS Update
          </Label>
          <Switch
            type="switch"
            id="sendSmsUpdate"
            name="sendSmsUpdate"
            value={sendSMS}
            onChange={() => setSendSMS(!sendSMS)}
          />
        </div>
        <div className="d-flex justify-content-end">
          <Button
            className="btn px-4"
            color="success"
            disabled={isPublishing || isEstimating || !newDate || isEstimateError || newDate === originalDate}
            onClick={onPublish}
          >
            {isPublishing ? <Spinner type="grow" size="sm" /> : <span>Publish</span>}
          </Button>
          <Button className="btn ms-2 px-4" onClick={onClose} disabled={isPublishing}>
            Close
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default Publish;
